// ----------------------------------------------------------------------------
// Ordergroove offer — per-day price, supply label, and purchase mode helpers
// ----------------------------------------------------------------------------
// Shared by the legacy buy box (`snippets/product-form/product-form-v2.js`) and
// the PDP Subscription Options block (`blocks/subscription-options.js`).
//
// The OG app owns the offer template, so both hosts read the prices out of it
// and write the per-day figures back in after every OG repaint. Supply and
// delivery days are resolved by the host (they come from product metafields and
// the selected size), which is why they arrive here as plain numbers.
// ----------------------------------------------------------------------------
// Gotchas:
// - OG opt-in / opt-out buttons live in shadow DOM, so `closest()` from a click
//   target misses them. Mode detection walks `composedPath()` instead.
// - OG can keep `selling_plan` populated while one-time is selected, which is
//   why callers pass a click hint rather than trusting the input alone.
// - Labels are templated so the storefront can localise them. Defaults keep the
//   legacy buy box output byte-identical to what it shipped with.
// ----------------------------------------------------------------------------

import { formatMoney } from '@shopify/theme-currency';

// -------------------------------------
// Constants
// -------------------------------------

export var PRICE_TOKEN = '__PRICE__';
export var SUPPLY_TOKEN = '__SUPPLY__';
export var DELIVERY_TOKEN = '__DELIVERY__';
var DEFAULT_PER_DAY_TEMPLATE = "".concat(PRICE_TOKEN, "/day");
var DEFAULT_SUPPLY_TEMPLATE = "Delivered every ".concat(DELIVERY_TOKEN, " days (").concat(SUPPLY_TOKEN, " day supply)");
var SUBSCRIPTION_ROW = '.selling-plan__option:not(.selling-plan__option--one-time)';
var ONE_TIME_ROW = '.selling-plan__option--one-time';
var PER_DAY_PRICE = '.per-day__price';
var PER_DAY_LABEL = '.selling-plan-per-day__label';
var PER_DAY_DELIVERY_CLASS = 'selling-plan-per-day__delivery';
var ONE_TIME_SUBTITLE = '.selling-plan__subtitle--one-time';
var SUPPLY_DELIVERY_PARENS_RE = /\([^)]+\)/;
var SELLING_PLAN_GROUP = '.selling-plan';
var HYDRATION_MAX_TRIES = 20;
var HYDRATION_INTERVAL_MS = 200;

// -------------------------------------
// Money helpers
// -------------------------------------

export function formatMoneyCents(cents) {
  var _window$theme;
  var amount = Number(cents);
  if (Number.isNaN(amount)) return '';
  var format = ((_window$theme = window.theme) === null || _window$theme === void 0 ? void 0 : _window$theme.settingCurrencyFormat) || '{{amount}}';
  return formatMoney(amount, format);
}

/**
 * Parse a currency string from OG fallback text into cents.
 */
export function parseMoneyToCents(value) {
  if (!value) return null;

  // Keep digits, comma, dot, minus. Assume the last separator is the decimal one.
  var cleaned = String(value).trim().replace(/[^\d.,-]/g, '');
  if (!cleaned) return null;
  var decimalIndex = Math.max(cleaned.lastIndexOf('.'), cleaned.lastIndexOf(','));
  var normalized = cleaned;
  if (decimalIndex >= 0) {
    var intPart = cleaned.slice(0, decimalIndex).replace(/[.,]/g, '');
    var decPart = cleaned.slice(decimalIndex + 1).replace(/[.,]/g, '').slice(0, 2);
    normalized = "".concat(intPart, ".").concat(decPart);
  } else {
    normalized = cleaned.replace(/[.,]/g, '');
  }
  var amount = Number(normalized);
  return Number.isNaN(amount) ? null : Math.round(amount * 100);
}

// -------------------------------------
// Offer reads
// -------------------------------------

/**
 * Price of the given purchase mode, in cents, as currently painted by OG.
 */
export function getOfferCents(offer, mode) {
  if (!offer) return null;
  if (mode === 'subscription') {
    var subscription = offer.querySelector("".concat(SUBSCRIPTION_ROW, " og-price[subscription] [slot=\"fallback\"]")) || offer.querySelector('og-price[subscription] [slot="fallback"]');
    return parseMoneyToCents(subscription === null || subscription === void 0 ? void 0 : subscription.textContent);
  }
  var oneTime = offer.querySelector("".concat(ONE_TIME_ROW, " og-price.og-price--one-time[regular] [slot=\"fallback\"]")) || offer.querySelector("".concat(ONE_TIME_ROW, " og-price.og-price--one-time [slot=\"fallback\"]")) || offer.querySelector("".concat(ONE_TIME_ROW, " og-price[regular] [slot=\"fallback\"]"));
  return parseMoneyToCents(oneTime === null || oneTime === void 0 ? void 0 : oneTime.textContent);
}
export function offerHasSellingUi(offer) {
  var _offer$querySelector;
  return !!(offer !== null && offer !== void 0 && (_offer$querySelector = offer.querySelector) !== null && _offer$querySelector !== void 0 && _offer$querySelector.call(offer, SELLING_PLAN_GROUP));
}

/**
 * Supply label with the parenthetical delivery cadence wrapped for styling.
 * e.g. `Delivered every 45 days <span …>(45 day supply)</span>`
 */
export function formatSupplyLabelHtml(supplyDays, deliveryDays, supplyTemplate) {
  var plain = supplyTemplate.replace(SUPPLY_TOKEN, String(supplyDays)).replace(DELIVERY_TOKEN, String(deliveryDays));
  return plain.replace(SUPPLY_DELIVERY_PARENS_RE, "<span class=\"".concat(PER_DAY_DELIVERY_CLASS, "\">$&</span>"));
}

// -------------------------------------
// Offer writes
// -------------------------------------

/**
 * Rewrite the per-day price on each purchase option row, plus the supply label.
 * A missing selector is left alone: the app's own value stays on screen.
 */
export function syncOfferPerDay() {
  var {
    offer,
    supplyDays,
    deliveryDays,
    perDayTemplate = DEFAULT_PER_DAY_TEMPLATE,
    supplyTemplate = DEFAULT_SUPPLY_TEMPLATE
  } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  if (!offer || !supplyDays) return;
  var perDay = cents => perDayTemplate.replace(PRICE_TOKEN, formatMoneyCents(Math.round(cents / supplyDays)));
  var subscriptionCents = getOfferCents(offer, 'subscription');
  if (subscriptionCents != null) {
    offer.querySelectorAll("".concat(SUBSCRIPTION_ROW, " ").concat(PER_DAY_PRICE)).forEach(element => {
      element.textContent = perDay(subscriptionCents);
    });
  }
  var oneTimeCents = getOfferCents(offer, 'one_time');
  if (oneTimeCents != null) {
    offer.querySelectorAll("".concat(ONE_TIME_ROW, " ").concat(PER_DAY_PRICE)).forEach(element => {
      element.textContent = perDay(oneTimeCents);
    });
  }
  if (!deliveryDays) return;
  var supplyLabelHtml = formatSupplyLabelHtml(supplyDays, deliveryDays, supplyTemplate);
  offer.querySelectorAll(PER_DAY_LABEL).forEach(element => {
    element.innerHTML = supplyLabelHtml;
  });
}

/**
 * Caption under the one-time purchase title (e.g. shipping notice). OG owns the
 * opt-out label; the theme inserts this after every repaint.
 */
export function syncOneTimeSubtitle() {
  var {
    offer,
    copy
  } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var text = copy === null || copy === void 0 ? void 0 : copy.trim();
  if (!offer || !text) return;
  offer.querySelectorAll("".concat(ONE_TIME_ROW, " og-optout-button")).forEach(button => {
    var row = button.closest(ONE_TIME_ROW);
    if (!row) return;
    var subtitle = row.querySelector(ONE_TIME_SUBTITLE);
    if (!subtitle) {
      subtitle = document.createElement('span');
      subtitle.className = 'selling-plan__subtitle selling-plan__subtitle--one-time';
      button.parentNode.insertBefore(subtitle, button.nextSibling);
    }
    if (subtitle.textContent !== text) {
      subtitle.textContent = text;
    }
  });
}

/**
 * OG hydrates asynchronously, so poll briefly and let the host re-sync as soon
 * as prices (or per-day placeholders) exist.
 */
export function waitForOfferPrices(resolveOffer, onReady) {
  var tries = 0;
  var tick = () => {
    var _offer$querySelector2, _offer$querySelector3, _offer$querySelector4;
    tries += 1;
    var offer = typeof resolveOffer === 'function' ? resolveOffer() : resolveOffer;
    var hasPrices = !!(offer !== null && offer !== void 0 && (_offer$querySelector2 = offer.querySelector) !== null && _offer$querySelector2 !== void 0 && _offer$querySelector2.call(offer, 'og-price[subscription] [slot="fallback"]')) || !!(offer !== null && offer !== void 0 && (_offer$querySelector3 = offer.querySelector) !== null && _offer$querySelector3 !== void 0 && _offer$querySelector3.call(offer, "".concat(ONE_TIME_ROW, " og-price[regular] [slot=\"fallback\"]")));
    var hasPerDay = !!(offer !== null && offer !== void 0 && (_offer$querySelector4 = offer.querySelector) !== null && _offer$querySelector4 !== void 0 && _offer$querySelector4.call(offer, PER_DAY_PRICE));
    if (offer && (hasPrices || hasPerDay)) {
      onReady(offer);
    }
    if (tries < HYDRATION_MAX_TRIES && (!hasPrices || !hasPerDay)) {
      window.setTimeout(tick, HYDRATION_INTERVAL_MS);
    }
  };
  tick();
}

// -------------------------------------
// Purchase mode
// -------------------------------------

/**
 * Purchase mode implied by a click on an OG opt-in / opt-out button, or null
 * when the click was somewhere else. Generic selling-plan rows are ignored:
 * they can fire before the radios update.
 */
export function findOgPurchaseModeFromEvent(event, offer) {
  if (!offer) return null;
  var path = typeof event.composedPath === 'function' ? event.composedPath() : [event.target];
  var isInOffer = node => node instanceof HTMLElement && offer.contains(node);
  if (path.some(node => isInOffer(node) && node.tagName === 'OG-OPTOUT-BUTTON')) return 'one_time';
  if (path.some(node => isInOffer(node) && node.tagName === 'OG-OPTIN-BUTTON')) return 'subscription';
  return null;
}

/**
 * Whether the customer is on one-time purchase.
 *
 * - OG radios are often not queryable from document JS (shadow DOM), so `:checked` alone is unreliable.
 * - OG may keep `selling_plan` non-empty on one-time, hence the click `hint`.
 * - Before OG renders any selling UI, treat the customer as subscribed to match the Liquid default.
 */
export function isOneTimeSelected() {
  var {
    offer,
    sellingPlanInput,
    hint
  } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  if (hint === 'one_time') return true;
  if (hint === 'subscription') return false;
  if (offer) {
    if (offer.querySelector("".concat(ONE_TIME_ROW, " input[type=\"radio\"]:checked"))) return true;
    if (offer.querySelector("".concat(SUBSCRIPTION_ROW, " input[type=\"radio\"]:checked"))) return false;
  }
  if (!offerHasSellingUi(offer)) return false;
  if (!sellingPlanInput) return false;
  return !sellingPlanInput.value;
}